/**
 * LevelCueDriver — HUD guidance for a level's puzzle chain.
 *
 * Replaces the per-level "sticky prompt" paragraphs (one wrapped panel per
 * puzzle over the middle of the screen, e.g. "Walk right to the seized
 * crane. Minna stands on the brass plate … then tap 2 in rhythm with the
 * marker.") with what Level 0 uses: a short objective line at the top and
 * a "who · what to press" chip pinned to the thing to act on.
 *
 * The active puzzle is the first unsolved one in the level's order. Its
 * `getCue()` says what to show for its current step; puzzles without one
 * fall back to the entry's objective text and no chip. The driver polls
 * a few times a second (cheap: the HUD only rebuilds when the cue object
 * changes, and only moves the chip when the anchor does) and also
 * refreshes immediately on puzzle events.
 */
import type { Scene } from "@babylonjs/core/scene";
import type { Observer } from "@babylonjs/core/Misc/observable";

import { GameEvents } from "../config/events";
import type { EventBus } from "../core/EventBus";
import type { CueDef } from "../ui/tutorialCues";
import type { PuzzleCue, PuzzleModule } from "./PuzzleModule";

export interface LevelCueEntry {
  puzzle: Pick<PuzzleModule, "id" | "getState" | "getCue">;
  /** Objective line when the puzzle has no getCue (a few words). */
  objective: string;
}

type Anchor = NonNullable<PuzzleCue["anchor"]>;

/** Re-send the anchor once it has moved this far (world units). */
const ANCHOR_EPSILON = 0.2;
/** Poll every N simulation steps (60 Hz → 10 Hz). */
const POLL_STEPS = 6;

export class LevelCueDriver {
  private started = false;
  private lastIdx = -1;
  private lastObjective = "";
  private lastCue: CueDef | null = null;
  private lastAnchor: Anchor | undefined;
  private steps = 0;
  private observer: Observer<Scene> | null = null;
  private readonly unsubs: Array<() => void> = [];

  /**
   * @param finale  Guidance once every puzzle is solved but the level
   *                isn't over yet (L3: walk into the portal together).
   */
  constructor(
    private readonly entries: readonly LevelCueEntry[],
    private readonly events: EventBus,
    private readonly finale?: () => PuzzleCue | null,
  ) {}

  /**
   * Wire into a scene: start after the opening cutscene (its dialogue
   * would otherwise sit on top of the chips), refresh on puzzle events,
   * poll on the fixed simulation step.
   */
  attach(scene: Scene): this {
    this.unsubs.push(
      this.events.on(GameEvents.CUTSCENE_ENDED, () => {
        if (!this.started) this.start();
      }),
      this.events.on(GameEvents.PUZZLE_SOLVED, () => this.refresh()),
      this.events.on(GameEvents.PUZZLE_PROGRESSED, () => this.refresh()),
    );
    this.observer = scene.onBeforeStepObservable.add(() => {
      if (++this.steps % POLL_STEPS === 0) this.refresh();
    });
    return this;
  }

  start(): void {
    this.started = true;
    this.refresh();
  }

  /** Index of the active (first unsolved) puzzle; entries.length when all are solved. */
  activeIndex(): number {
    for (let i = 0; i < this.entries.length; i++) {
      if (!this.entries[i].puzzle.getState().solved) return i;
    }
    return this.entries.length;
  }

  refresh(): void {
    if (!this.started) return;
    const n = this.entries.length;
    const idx = this.activeIndex();
    let pc: PuzzleCue | null;
    let objective: string;
    if (idx >= n) {
      pc = this.finale?.() ?? null;
      if (!pc) {
        if (this.lastIdx !== n || this.lastObjective || this.lastCue) {
          this.lastIdx = n;
          this.clear();
        }
        return;
      }
      objective = pc.objective;
    } else {
      pc = this.entries[idx].puzzle.getCue?.() ?? null;
      objective = `(${idx + 1}/${n}) ${pc?.objective ?? this.entries[idx].objective}`;
    }
    if (idx < n && idx !== this.lastIdx) {
      this.lastIdx = idx;
      this.events.emit(GameEvents.PUZZLE_PROGRESSED, { current: idx + 1, total: n });
    }
    if (objective !== this.lastObjective) {
      this.lastObjective = objective;
      this.events.emit(GameEvents.HUD_SET_OBJECTIVE, { text: objective });
    }
    const cue = pc?.cue ?? null;
    const anchor = cue ? pc?.anchor : undefined;
    if (cue !== this.lastCue || anchorMoved(anchor, this.lastAnchor)) {
      this.lastCue = cue;
      this.lastAnchor = anchor ? { x: anchor.x, y: anchor.y, z: anchor.z } : undefined;
      this.events.emit(GameEvents.HUD_SET_CUE, { slot: "objective", cue, anchor: this.lastAnchor });
    }
  }

  private clear(): void {
    this.lastObjective = "";
    this.lastCue = null;
    this.lastAnchor = undefined;
    this.events.emit(GameEvents.HUD_SET_OBJECTIVE, { text: "" });
    this.events.emit(GameEvents.HUD_SET_CUE, { slot: "objective", cue: null });
  }

  dispose(): void {
    for (const u of this.unsubs.splice(0)) u();
    this.observer?.remove();
    this.observer = null;
    if (this.started) this.clear();
    this.started = false;
  }
}

function anchorMoved(a: Anchor | undefined, b: Anchor | undefined): boolean {
  if (!a || !b) return a !== b;
  const dx = a.x - b.x, dy = a.y - b.y, dz = a.z - b.z;
  return dx * dx + dy * dy + dz * dz > ANCHOR_EPSILON * ANCHOR_EPSILON;
}
